import React, { useEffect, useState, useCallback } from 'react';
import { Save, Trash2, Download, Check, Loader2, Cpu, KeyRound } from 'lucide-react';
import settingsService, { ProviderStatus, ModelsInfo, PullStatus } from '../../services/settingsService';
import { PageHeader, Spinner, Button, IconButton } from '../../components/ui';
import '../../styles/theme.css';

interface Field { key: string; label: string; secret?: boolean; placeholder?: string; }
interface Group { title: string; note: string; tone: 'public' | 'infra'; fields: Field[]; }

const GROUPS: Group[] = [
  { title: 'Anthropic · Claude (public API)', tone: 'public', note: 'Usable on documents marked public.',
    fields: [{ key: 'ANTHROPIC_API_KEY', label: 'API key', secret: true, placeholder: 'sk-ant-…' },
             { key: 'ANTHROPIC_MODEL', label: 'Model (optional)', placeholder: 'claude-sonnet-4-6' }] },
  { title: 'OpenAI · GPT (public API)', tone: 'public', note: 'Usable on documents marked public.',
    fields: [{ key: 'OPENAI_API_KEY', label: 'API key', secret: true, placeholder: 'sk-…' },
             { key: 'OPENAI_MODEL', label: 'Model (optional)', placeholder: 'gpt-4o' }] },
  { title: 'Amazon Bedrock · Claude (in-tenant)', tone: 'infra', note: 'Compliant for confidential documents.',
    fields: [{ key: 'BEDROCK_MODEL_ID', label: 'Model id', placeholder: 'anthropic.claude-3-5-sonnet-…' },
             { key: 'AWS_REGION', label: 'Region', placeholder: 'us-east-1' },
             { key: 'AWS_ACCESS_KEY_ID', label: 'Access key id', secret: true },
             { key: 'AWS_SECRET_ACCESS_KEY', label: 'Secret access key', secret: true }] },
  { title: 'Azure OpenAI · GPT (in-tenant)', tone: 'infra', note: 'Compliant for confidential documents.',
    fields: [{ key: 'AZURE_OPENAI_ENDPOINT', label: 'Endpoint', placeholder: 'https://….openai.azure.com' },
             { key: 'AZURE_OPENAI_DEPLOYMENT', label: 'Deployment', placeholder: 'gpt-4o' },
             { key: 'AZURE_OPENAI_API_KEY', label: 'API key', secret: true }] },
  { title: 'LaptopPool · managed fine-tuning + OSS serving', tone: 'public',
    note: 'Runs fine-tune jobs and serves a promoted model for public documents. Public-only (external).',
    fields: [{ key: 'LAPTOPOOL_KEY', label: 'API key', secret: true, placeholder: 'lpk_…' },
             { key: 'LAPTOPOOL_MODEL', label: 'Served model (optional — set automatically on promotion)', placeholder: 'auto' }] },
];

const Settings: React.FC = () => {
  const [status, setStatus] = useState<Record<string, ProviderStatus>>({});
  const [available, setAvailable] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [models, setModels] = useState<ModelsInfo | null>(null);
  const [pulls, setPulls] = useState<PullStatus>({});
  const [addName, setAddName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const refresh = useCallback(async () => {
    const [p, m] = await Promise.all([settingsService.getProviders(), settingsService.getModels()]);
    setStatus(p.status); setAvailable(p.available); setModels(m);
  }, []);

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  // Poll pull progress while anything is downloading.
  useEffect(() => {
    const anyPulling = Object.values(pulls).some((p) => !p.done) ||
      (models?.recommended.some((r) => !r.installed) ?? false);
    if (!anyPulling) return;
    const t = setInterval(async () => {
      const ps = await settingsService.pullStatus();
      setPulls(ps);
      if (Object.values(ps).some((p) => p.done)) refresh();
    }, 1500);
    return () => clearInterval(t);
  }, [pulls, models, refresh]);

  const saveProviders = async () => {
    const dirty = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim()));
    if (!Object.keys(dirty).length) { setMsg('Nothing to save.'); return; }
    setSaving(true); setMsg('');
    try {
      const res = await settingsService.setProviders(dirty);
      setStatus(res.status); setAvailable(res.available); setValues({});
      setMsg(`Saved. Available providers: ${res.available.join(', ') || 'none'}.`);
    } catch (e: any) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const clearField = async (key: string) => {
    await settingsService.deleteProvider(key);
    refresh();
  };

  const pull = async (name: string) => {
    if (!name.trim()) return;
    await settingsService.pullModel(name.trim());
    setPulls((p) => ({ ...p, [name]: { status: 'pulling', percent: 0, done: false, error: null } }));
    setAddName('');
  };

  const setActive = async (name: string) => {
    await settingsService.setActiveModel(name);
    refresh();
    setMsg(`Active OSS model set to ${name}.`);
  };

  if (loading) return <div><PageHeader title="Settings" /><div className="mx-card mx-card-pad"><Spinner label="Loading…" /></div></div>;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure frontier + managed provider keys and the open-source model. Keys are encrypted at rest and shown masked." />
      {msg && <div className="mx-card mx-card-pad" style={{ marginBottom: 16, fontSize: 13 }}>{msg}</div>}

      {/* Frontier providers */}
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
        <KeyRound size={17} /> Provider keys</h2>
      <div className="mx-grid" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 12 }}>
        {GROUPS.map((g) => (
          <div key={g.title} className="mx-card">
            <div className="mx-card-head">
              <h3 className="mx-card-title">{g.title}</h3>
              <span className="mx-pill" style={{
                background: g.tone === 'infra' ? 'var(--mx-success-weak)' : 'var(--mx-info-weak)',
                color: g.tone === 'infra' ? 'var(--mx-success)' : 'var(--mx-info)' }}>
                {g.tone === 'infra' ? 'confidential-ok' : 'public-only'}</span>
            </div>
            <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="mx-subtle" style={{ fontSize: 12 }}>{g.note}</div>
              {g.fields.map((f) => {
                const st = status[f.key];
                return (
                  <div key={f.key}>
                    <label className="mx-subtle" style={{ fontSize: 12, fontWeight: 600 }}>{f.label}
                      {st?.configured && <span style={{ color: 'var(--mx-success)', marginLeft: 8 }}>
                        · set {f.secret ? `(${st.preview})` : `(${st.preview})`}</span>}</label>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <input className="mx-input" type={f.secret ? 'password' : 'text'}
                        placeholder={st?.configured ? '•••••• (leave blank to keep)' : (f.placeholder || '')}
                        value={values[f.key] || ''} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} />
                      {st?.configured && <IconButton icon={Trash2} label="Clear"
                        style={{ color: 'var(--mx-danger)' }} onClick={() => clearField(f.key)} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24 }}>
        <Button icon={Save} variant="primary" onClick={saveProviders} disabled={saving}>
          {saving ? 'Saving…' : 'Save keys'}</Button>
        <span className="mx-subtle" style={{ fontSize: 12 }}>
          Available now: {available.length ? available.join(', ') : 'ollama only'}</span>
      </div>

      {/* OSS models */}
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
        <Cpu size={17} /> Open-source model (OSS stream)</h2>
      {!models?.ollama_up && (
        <div className="mx-card mx-card-pad" style={{ marginBottom: 12, color: 'var(--mx-warning)', fontSize: 13 }}>
          Ollama isn't reachable — start it to manage local models.</div>
      )}
      <div className="mx-card" style={{ marginBottom: 12 }}>
        <div className="mx-card-head"><h3 className="mx-card-title">Active model</h3></div>
        <div className="mx-card-pad" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select className="mx-select" style={{ maxWidth: 280 }} value={models?.active || ''}
            onChange={(e) => setActive(e.target.value)}>
            {(models?.installed || []).map((m) => <option key={m} value={m}>{m}</option>)}
            {models && !models.installed.includes(models.active) && <option value={models.active}>{models.active} (not installed)</option>}
          </select>
          <span className="mx-subtle" style={{ fontSize: 12 }}>the OSS extraction stream uses this model</span>
        </div>
      </div>

      <div className="mx-card">
        <div className="mx-card-head"><h3 className="mx-card-title">Recommended for extraction</h3></div>
        <div className="mx-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(models?.recommended || []).map((r) => {
            const ps = pulls[r.name];
            return (
              <div key={r.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div><span className="mx-mono" style={{ fontWeight: 600 }}>{r.name}</span>
                  <span className="mx-subtle" style={{ fontSize: 12, marginLeft: 8 }}>{r.note}</span></div>
                {r.installed ? (
                  models?.active === r.name
                    ? <span className="mx-pill" style={{ background: 'var(--mx-success-weak)', color: 'var(--mx-success)' }}><Check size={12} /> active</span>
                    : <Button icon={Check} variant="secondary" size="sm" onClick={() => setActive(r.name)}>Set active</Button>
                ) : ps && !ps.done ? (
                  <span className="mx-chip"><Loader2 size={12} className="mx-spin" /> {ps.status} {ps.percent}%</span>
                ) : (
                  <Button icon={Download} variant="secondary" size="sm" onClick={() => pull(r.name)}>Pull</Button>
                )}
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, borderTop: '1px solid var(--mx-border)', paddingTop: 12 }}>
            <input className="mx-input mx-mono" placeholder="add a model — ollama name or hf.co/<repo> (GGUF)"
              value={addName} onChange={(e) => setAddName(e.target.value)} />
            <Button icon={Download} variant="primary" size="sm" onClick={() => pull(addName)} disabled={!addName.trim()}>
              Add</Button>
          </div>
          {Object.entries(pulls).filter(([, p]) => p.error).map(([n, p]) => (
            <div key={n} style={{ color: 'var(--mx-danger)', fontSize: 12 }}>{n}: {p.error}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Settings;
